/**
 * Bookkeeping for "I pressed the button; has the signal arrived yet?".
 *
 * Pure, so the counting and expiry rules can be tested without timers, a query
 * client or a rendered tree. The hook in `./hooks` owns the polling and the
 * clock; everything that decides *what* is still outstanding lives here.
 */

import type { TestSignalPath } from "../client-messages/test-signals";
import type { TestSignalLogItem } from "../client-messages/test-signals";

/** Outstanding or arrived signals, per delivery leg. */
export type SignalPathCounts = Readonly<Partial<Record<TestSignalPath, number>>>;

/** How long a single press keeps the page watching. Each further press restarts it. */
export const WATCH_WINDOW_MS = 5 * 60 * 1000;

/**
 * How often the log is re-read while watching.
 *
 * A signal takes a minute or two to appear, so ten seconds surfaces one
 * promptly at half the requests a five second poll would cost.
 */
export const WATCH_POLL_INTERVAL_MS = 10 * 1000;

/**
 * The line between "was already there" and "arrived because I pressed".
 *
 * A timestamp rather than a count of rows, because "Load more" appends older
 * rows that a count would read as arrivals. `"none"` needs a floor for the same
 * reason — an empty first page is not an empty log.
 *
 * Deliberately not the press time: `occurredAt` is the clip's own timestamp,
 * from a device whose clock can lag the cloud's, so a skewed device's signal
 * would never count and the page would report a failure that did not happen.
 */
export type Watermark =
  /** Newest manual signal known at press time; a later one is an arrival. */
  | { readonly kind: "at"; readonly occurredAt: string }
  /** The log held no manual signal, so any manual signal newer than the oldest row read then counts. */
  | { readonly kind: "none"; readonly oldestLoadedAt: string | null };

export interface SignalWatch {
  /** Presses so far this window, per leg. Never decremented — arrivals are subtracted at read time. */
  readonly expected: SignalPathCounts;
  /** Where this watch started counting from. See {@link Watermark}. */
  readonly watermark: Watermark;
  /** Epoch ms. Reset by every press, so the last press always gets a full window. */
  readonly deadline: number;
}

/** A watch and the camera it was armed for. */
export interface ArmedWatch {
  readonly subject: string;
  readonly watch: SignalWatch;
}

/**
 * The watch belonging to `subject`, or `null` when the armed watch belongs to
 * another camera.
 *
 * A watch names its camera because the page reuses one hook instance across
 * cameras when only the route params change. Scoring one camera's watch against
 * another's log files a verdict about a signal that was never sent there.
 */
export function watchForSubject(armed: ArmedWatch | null, subject: string): SignalWatch | null {
  return armed !== null && armed.subject === subject ? armed.watch : null;
}

const addOne = (counts: SignalPathCounts, path: TestSignalPath): SignalPathCounts => ({
  ...counts,
  [path]: (counts[path] ?? 0) + 1,
});

export function newestManualSignal(entries: readonly TestSignalLogItem[]): string | null {
  let newest: string | null = null;
  for (const entry of entries) {
    if (entry.kind !== "signal" || entry.triggerType !== "manual") continue;
    if (newest === null || entry.occurredAt > newest) newest = entry.occurredAt;
  }
  return newest;
}

/**
 * The oldest row the log had loaded — the floor for a `"none"` watermark.
 *
 * Spans every row kind, not just manual signals: the boundary is "what the log
 * had loaded", and commands and periodic rows mark it just as well.
 */
function oldestLoadedAt(entries: readonly TestSignalLogItem[]): string | null {
  let oldest: string | null = null;
  for (const entry of entries) {
    if (oldest === null || entry.occurredAt < oldest) oldest = entry.occurredAt;
  }
  return oldest;
}

/**
 * Begin watching, or extend an existing watch by one more press.
 *
 * The watermark is captured only on the press that starts a window, so a
 * second press does not discount a signal that arrived between the two. A
 * `"none"` watermark is a captured value like any other — it means "count any
 * manual signal", not "nothing captured yet" — so a later press keeps it rather
 * than re-reading the log and swallowing an arrival.
 *
 * `entries` must be the log as it stood at the press. The caller re-reads it
 * first, so this is never guessing from a cache.
 */
export function notePress(
  watch: SignalWatch | null,
  path: TestSignalPath,
  entries: readonly TestSignalLogItem[],
  now: number,
): SignalWatch {
  return {
    expected: addOne(watch?.expected ?? {}, path),
    watermark: watch === null ? capturedWatermark(entries) : watch.watermark,
    deadline: now + WATCH_WINDOW_MS,
  };
}

function capturedWatermark(entries: readonly TestSignalLogItem[]): Watermark {
  const newest = newestManualSignal(entries);
  if (newest !== null) return { kind: "at", occurredAt: newest };
  // `null` only when the log was loaded and completely empty — there is then no
  // earlier page to page back into, so no floor is needed.
  return { kind: "none", oldestLoadedAt: oldestLoadedAt(entries) };
}

/**
 * What has not arrived yet, per leg.
 *
 * Only manually-triggered signals count. A periodic heartbeat landing mid-window
 * is unrelated to the press, and letting it satisfy the watch would report
 * success for a signal that never came.
 */
export function outstanding(watch: SignalWatch, entries: readonly TestSignalLogItem[]): SignalPathCounts {
  const arrived: Partial<Record<TestSignalPath, number>> = {};
  for (const entry of entries) {
    if (entry.kind !== "signal" || entry.triggerType !== "manual") continue;
    if (watch.watermark.kind === "at" && entry.occurredAt <= watch.watermark.occurredAt) continue;
    // `<=` rather than `<`: by construction no loaded row was a manual signal,
    // so a manual row sharing that exact timestamp came from a page that was
    // not loaded — history, not an arrival.
    if (
      watch.watermark.kind === "none" &&
      watch.watermark.oldestLoadedAt !== null &&
      entry.occurredAt <= watch.watermark.oldestLoadedAt
    )
      continue;
    arrived[entry.signalPath] = (arrived[entry.signalPath] ?? 0) + 1;
  }

  const remaining: Partial<Record<TestSignalPath, number>> = {};
  for (const [path, count] of Object.entries(watch.expected)) {
    const left = count - (arrived[path as TestSignalPath] ?? 0);
    if (left > 0) remaining[path as TestSignalPath] = left;
  }
  return remaining;
}

/** Legs with something still expected, so a zero never reads as an outstanding leg. */
function positiveCounts(counts: SignalPathCounts): SignalPathCounts {
  const kept: Partial<Record<TestSignalPath, number>> = {};
  for (const [path, count] of Object.entries(counts)) {
    if (count > 0) kept[path as TestSignalPath] = count;
  }
  return kept;
}

/** Total across legs — `0` means every press has been answered. */
export function totalOutstanding(counts: SignalPathCounts): number {
  return Object.values(counts).reduce<number>((sum, n) => sum + n, 0);
}

export const WATCH_OUTCOMES = ["passed", "partial", "failed"] as const;

/**
 * How a closed window turned out.
 *
 * `partial` exists because the two-state reading is wrong often enough to
 * matter: pressing Camera and VA and getting only the camera signal back is
 * neither a working camera nor a broken one, and calling it either sends an
 * operator to the wrong place.
 */
export type WatchOutcome = (typeof WATCH_OUTCOMES)[number];

/**
 * One closed window and what it proved — the page's verdict on a press.
 *
 * Filed for every watch that closes, not only the ones that went wrong: a press
 * that worked is the answer the operator pressed the button for, and leaving it
 * silent makes success indistinguishable from the page having forgotten.
 *
 * Held only in component state: the log itself is the durable record, and a
 * report is a statement about what the operator watched for in this sitting.
 * Reloading or leaving the camera drops it.
 */
export interface WatchReport {
  /** Stable across re-renders so the row can be dismissed on its own. */
  readonly id: string;
  readonly outcome: WatchOutcome;
  /** Epoch ms the window closed — when the last signal landed, or the deadline. */
  readonly closedAt: number;
  /** What turned up, per leg. */
  readonly arrived: SignalPathCounts;
  /** What never did, per leg. Empty for a pass. */
  readonly missing: SignalPathCounts;
}

/**
 * The verdict for a window that expected `expected` and is still missing
 * `missing`.
 *
 * `>=` rather than `===` so an over-count reads as a failure rather than
 * falling through to `partial`.
 */
export function watchOutcome(expected: SignalPathCounts, missing: SignalPathCounts): WatchOutcome {
  const missingTotal = totalOutstanding(missing);
  if (missingTotal === 0) return "passed";
  return missingTotal >= totalOutstanding(expected) ? "failed" : "partial";
}

/** What arrived, per leg — `expected` less whatever is still `missing`. */
export function arrivedCounts(expected: SignalPathCounts, missing: SignalPathCounts): SignalPathCounts {
  const arrived: Partial<Record<TestSignalPath, number>> = {};
  for (const [path, count] of Object.entries(expected)) {
    const got = count - (missing[path as TestSignalPath] ?? 0);
    if (got > 0) arrived[path as TestSignalPath] = got;
  }
  return arrived;
}

/**
 * Everything about a closed window except its id, which only the caller holding
 * the list can allocate.
 */
export function reportFor(watch: SignalWatch, missing: SignalPathCounts, closedAt: number): Omit<WatchReport, "id"> {
  return {
    outcome: watchOutcome(watch.expected, missing),
    closedAt,
    arrived: arrivedCounts(watch.expected, missing),
    missing: positiveCounts(missing),
  };
}

export type WatchProgress =
  /** Every press answered. */
  | { readonly kind: "satisfied" }
  /** Still within the window, with these legs outstanding. Time left is the caller's to derive from `deadline`. */
  | { readonly kind: "watching"; readonly remaining: SignalPathCounts }
  /** The window closed with these legs never answered. */
  | { readonly kind: "expired"; readonly remaining: SignalPathCounts };

/**
 * Where a watch stands right now.
 *
 * Arrival is checked before expiry, so a signal that lands in the same tick the
 * window closes is a success rather than a reported failure.
 */
export function watchProgress(watch: SignalWatch, entries: readonly TestSignalLogItem[], now: number): WatchProgress {
  const remaining = outstanding(watch, entries);
  if (totalOutstanding(remaining) === 0) return { kind: "satisfied" };
  if (now >= watch.deadline) return { kind: "expired", remaining };
  return { kind: "watching", remaining };
}
